import { z } from "zod";
import { api } from "../helpers/apiClient";

// Re-export all types and schemas from the types file
export * from "../evals/types";

// Import types for API functions
import {
  TestCaseSchema,
  EvalResultSchema,
  PaginatedEvalResultsSchema,
  BatchRunResponseSchema,
  CriterionSchema,
  PlaygroundAgentsResponseSchema,
  PlaygroundAgentInfoSchema,
  AvailableModelsResponseSchema,
  AgentVersionsResponseSchema,
  EvaluationJobResponseSchema,
  EvaluationJobStatusSchema,
  type TestCase,
  type EvalResult,
  type PaginatedEvalResults,
  type BatchRunResponse,
  type Criterion,
  type CreateTestCaseRequest,
  type RunEvalRequest,
  type BatchRunEvalRequest,
  type CreateCriterionRequest,
  type PlaygroundAgentsResponse,
  type PlaygroundAgentInfo,
  type AvailableModelsResponse,
  type AgentVersionsResponse,
  type EvaluationJobResponse,
  type EvaluationJobStatus,
  type BatchEvaluationRequest,
  type UpdateAgentConfigRequest,
  DataMatesResponseSchema,
  KnowledgeBaseSchema,
  type DataMatesResponse,
  type KnowledgeBase,
} from "../evals/types";

// ============================================================================
// TEST CASES APIs
// ============================================================================

export const getTestCases = () =>
  api.get<TestCase[]>("/evals/test-cases", {}, 0, z.array(TestCaseSchema));

export const getTestCase = (testCaseId: string) =>
  api.get<TestCase>(`/evals/test-cases/${testCaseId}`, {}, 0, TestCaseSchema);

export const createTestCase = (data: CreateTestCaseRequest) =>
  api.post<TestCase>("/evals/test-cases", data, TestCaseSchema);

export const updateTestCase = (testCaseId: string, data: CreateTestCaseRequest) =>
  api.post<TestCase>(`/evals/test-cases/${testCaseId}/update`, data, TestCaseSchema);

export const deleteTestCase = (testCaseId: string) =>
  api.delete<{ message: string; id: string }>(`/evals/test-cases/${testCaseId}`, {});

// ============================================================================
// EXECUTION APIs
// ============================================================================

export const runEval = (data: RunEvalRequest) =>
  api.post<EvalResult>("/evals/run", data, EvalResultSchema);

export const batchRunEval = (data: BatchRunEvalRequest) =>
  api.post<BatchRunResponse>("/evals/batch-run", data, BatchRunResponseSchema);

// ============================================================================
// RESULTS APIs
// ============================================================================

export const getEvalResults = (testCaseId?: string, page = 1, pageSize = 1000) =>
  api.get<PaginatedEvalResults>(
    "/evals/results",
    {
      ...(testCaseId ? { test_case_id: testCaseId } : {}),
      page,
      page_size: pageSize,
    },
    0,
    PaginatedEvalResultsSchema
  );

export const getEvalResult = (resultId: string) =>
  api.get<EvalResult>(`/evals/results/${resultId}`, {}, 0, EvalResultSchema);

export const deleteEvalResult = (resultId: string) =>
  api.delete<{ message: string; id: string }>(`/evals/results/${resultId}`, {});

// ============================================================================
// CRITERIONS APIs
// ============================================================================

export const getCriterions = () =>
  api.get<Criterion[]>("/evals/criterions", {}, 0, z.array(CriterionSchema));

export const getCriterion = (criterionId: string) =>
  api.get<Criterion>(`/evals/criterions/${criterionId}`, {}, 0, CriterionSchema);

export const createCriterion = (data: CreateCriterionRequest) =>
  api.post<Criterion>("/evals/criterions", data, CriterionSchema);

export const updateCriterion = (criterionId: string, data: CreateCriterionRequest) =>
  api.post<Criterion>(`/evals/criterions/${criterionId}/update`, data, CriterionSchema);

export const deleteCriterion = (criterionId: string) =>
  api.delete<{ message: string; id: string }>(`/evals/criterions/${criterionId}`, {});

// ============================================================================
// PLAYGROUND AGENTS APIs
// ============================================================================

export const getPlaygroundAgents = () =>
  api.get<PlaygroundAgentsResponse>("/evals/agents", {}, 0, PlaygroundAgentsResponseSchema);

export const getPlaygroundAgentDetails = (agentName: string, includePrompt = false) =>
  api.get<PlaygroundAgentInfo>(
    `/evals/agents/${encodeURIComponent(agentName)}`,
    { include_prompt: includePrompt },
    0,
    PlaygroundAgentInfoSchema
  );

export const getAvailableModels = () =>
  api.get<AvailableModelsResponse>("/evals/models", {}, 0, AvailableModelsResponseSchema);

export const updateAgentConfig = (agentName: string, data: UpdateAgentConfigRequest) =>
  api.post<PlaygroundAgentInfo>(
    `/evals/agents/${encodeURIComponent(agentName)}/update`,
    data,
    PlaygroundAgentInfoSchema
  );

export const getAgentVersions = (agentName: string) =>
  api.get<AgentVersionsResponse>(
    `/evals/agents/${encodeURIComponent(agentName)}/versions`,
    {},
    0,
    AgentVersionsResponseSchema
  );

export const getAgentVersion = (agentName: string, version: number) =>
  api.get<PlaygroundAgentInfo>(
    `/evals/agents/${encodeURIComponent(agentName)}/versions/${version}`,
    {},
    0,
    PlaygroundAgentInfoSchema
  );

// ============================================================================
// BATCH EVALUATION APIs
// ============================================================================

export const submitBatchEvaluation = (data: BatchEvaluationRequest) =>
  api.post<EvaluationJobResponse>("/evals/evaluate", data, EvaluationJobResponseSchema);

export const getEvaluationJobStatus = (jobId: string) =>
  api.get<EvaluationJobStatus>(`/evals/jobs/${jobId}/status`, {}, 0, EvaluationJobStatusSchema);

// ============================================================================
// DATAMATES & KNOWLEDGE BASES APIs
// ============================================================================

export const getDatamates = () =>
  api.get<DataMatesResponse>("/datamates/", {}, 0, DataMatesResponseSchema);

export const getKnowledgeBaseSources = () =>
  api.get<KnowledgeBase[]>("/knowledge_bases/", {}, 0, z.array(KnowledgeBaseSchema));
